import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppInsightsService } from './app-insights.service';
import { AccountInfo, InteractionRequiredAuthError, SilentRequest } from '@azure/msal-browser';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private accessToken = '';
  private userInfo = new BehaviorSubject<any>(null);
  origin = environment.apiOrigin;

  constructor(
    private msalService: MsalService,
    private http: HttpClient,
    private appInsights: AppInsightsService
  ) {}

  clearAccessToken() {
    this.accessToken = '';
    this.userInfo.next(null);
  }

  private getActiveAccount(): AccountInfo | null {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      const allAccounts = this.msalService.instance.getAllAccounts();
      if (allAccounts.length > 0) {
        activeAccount = allAccounts[0];
        this.msalService.instance.setActiveAccount(activeAccount);
      }
    }

    return activeAccount;
  }

  async acquireAccessToken(forceRefresh = false): Promise<string> {
    const activeAccount = this.getActiveAccount();

    if (!activeAccount) {
      this.clearAccessToken();
      await this.startInteractiveLogin();
      throw new Error('AUTH_INTERACTION_REQUIRED');
    }

    const silentRequest: SilentRequest = {
      scopes: [`${environment.scopeUri}/${environment.scope}`],
      account: activeAccount,
      forceRefresh,
    };

    try {
      const result = await firstValueFrom(this.msalService.acquireTokenSilent(silentRequest));
      this.setToken = result.accessToken;
      return result.accessToken;
    } catch (error) {
      this.clearAccessToken();
      this.appInsights.logException(error, 'token.service.ts', 'acquireAccessToken');

      if (error instanceof InteractionRequiredAuthError || this.isInteractionRequiredError(error)) {
        await this.startInteractiveLogin();
        throw new Error('AUTH_INTERACTION_REQUIRED');
      }

      throw error;
    }
  }

  async startInteractiveLogin(): Promise<void> {
    this.clearAccessToken();
    sessionStorage.setItem('contractai_auth_interrupted', 'true');
    const authRequest = {
      scopes: [`${environment.scopeUri}/${environment.scope}`],
    };

    this.appInsights.logInfo('Interactive login required', {
      source: 'token.service.ts',
      function: 'startInteractiveLogin'
    });

    await firstValueFrom(this.msalService.loginRedirect(authRequest));
  }

  private isInteractionRequiredError(error: any): boolean {
    const errorText = [
      error?.errorCode,
      error?.errorMessage,
      error?.message,
      String(error || '')
    ].join(' ').toLowerCase();

    return errorText.includes('interaction_required')
      || errorText.includes('login_required')
      || errorText.includes('consent_required')
      || errorText.includes('no_account');
  }

  async setAccessToken() {
    try {
      await this.acquireAccessToken();
      this.appInsights.logInfo('Access token acquired', {
        source: 'token.service.ts',
        function: 'setAccessToken'
      });
      this.fetchUserInfo();
    } catch (error) {
      this.appInsights.logException(error, 'token.service.ts', 'setAccessToken (outer catch)');
    }
  }

  // attempt at error fix
  // async setAccessToken() {
  // try {
  //   // Ensure there's an active account
  //   let activeAccount = this.msalService.instance.getActiveAccount();

  //   if (!activeAccount) {
  //     const allAccounts = this.msalService.instance.getAllAccounts();
  //     if (allAccounts.length > 0) {
  //       activeAccount = allAccounts[0];
  //       this.msalService.instance.setActiveAccount(activeAccount);
  //     } else {
  //       this.appInsights.logException("No MSAL account found. User may not be signed in.", 'token.service.ts', 'setAccessToken');
  //       return; // Exit early if no user is signed in
  //     }
  //   }

  //   const silentRequest = {
  //     scopes: [`${environment.scopeUri}/${environment.scope}`],
  //     account: activeAccount,
  //   };

  //   this.msalService.acquireTokenSilent(silentRequest).subscribe({
  //     next: (r) => {
  //       this.setToken = r.accessToken;
  //       this.appInsights.logInfo('Access token acquired', {
  //         source: 'token.service.ts',
  //         function: 'setAccessToken'
  //       });
  //       this.fetchUserInfo();
  //     },
  //     error: (err) => {
  //       this.appInsights.logException(err, 'token.service.ts', 'setAccessToken');

  //       // Optional fallback: force login if silent auth fails
  //       if (err.errorCode === 'no_account_error' || err.errorMessage?.includes('interaction_required')) {
  //         this.msalService.acquireTokenRedirect(silentRequest);
  //       }
  //     }
  //   });
  //   } catch (error) {
  //     this.appInsights.logException(error, 'token.service.ts', 'setAccessToken (outer catch)');
  //   }
  // }


  private set setToken(val: string) {
    this.accessToken = val;
  }

  get getToken(): string {
    return this.accessToken;
  }

  get getUserInfo(): Observable<any> {
    return this.userInfo.asObservable();
  }

  private fetchUserInfo() {
    if (!this.accessToken) {
      this.appInsights.logException('No access token found front', 'token.service.ts', 'fetchUserInfo');
      return;
    }

    const headers = { Authorization: `Bearer ${this.accessToken}` };
    this.http.get(`${this.origin}/user-info`, { headers }).subscribe({
      next: (info) => {
        this.userInfo.next(info);
        this.appInsights.logInfo('User info fetched front', {
          source: 'token.service.ts',
          function: 'fetchUserInfo',
          userName: this.userInfo || 'unknown'
        });
      },
      error: (err) => {
        this.appInsights.logException(err, 'token.service.ts', 'fetchUserInfo');
      }
    });
  }
}

// Previous version of TokenService without AppInsights integration
// @Injectable({
//   providedIn: 'root',
// })
// export class TokenService {
//   private accessToken = '';
//   private userInfo = new BehaviorSubject<any>(null);
//   origin = environment.apiOrigin

//   constructor(private msalService: MsalService, private http: HttpClient) {}

//   async setAccessToken() {
//     try {
//       this.msalService
//         .acquireTokenSilent({
//           scopes: [`${environment.scopeUri}/${environment.scope}`],
//           account: this.msalService.instance.getAllAccounts()[0],
//         })
//         .subscribe((r) => {
//           this.setToken = r.accessToken;
//           this.fetchUserInfo(); // Fetch user info when token is set
//         });
//     } catch (error) {
//       console.error('Error acquiring token:', error);
//     }
//   }

//   // Refresh error trial
//   private set setToken(val: string) {
//     this.accessToken = val;
//     // localStorage.setItem('accessToken', val); // Save token in localStorage
//   }

//   // Refresh error trial
//   get getToken(): string {
//     return this.accessToken;
//     //return this.accessToken || localStorage.getItem('accessToken') || '';
//   }

//   get getUserInfo(): Observable<any> {
//     return this.userInfo.asObservable();
//   }

//   private fetchUserInfo() {
//     if (!this.accessToken) {
//       console.error('No access token found. Unable to fetch user info.');
//       return;
//     }

//     const headers = { Authorization: `Bearer ${this.accessToken}` };
//     this.http.get(`${this.origin}/user-info`, { headers }).subscribe(
//       (info) => this.userInfo.next(info),
//       (error) => console.error('Error fetching user info:', error)
//     );
//   }
// }




/////// New trial for tokenService ////////////////
// import { Injectable } from '@angular/core';
// import { MsalService } from '@azure/msal-angular';
// import { environment } from '../environments/environment';
// import { HttpClient } from '@angular/common/http';
// import { BehaviorSubject, Observable } from 'rxjs';
// import { tap } from 'rxjs/operators';
// import { SilentRequest, AccountInfo } from '@azure/msal-browser'; // If needed

// @Injectable({
//   providedIn: 'root',
// })
// export class TokenService {
//   private accessToken = '';
//   private userInfo = new BehaviorSubject<any>(null);
//   private origin = environment.apiOrigin;

//   constructor(
//     private msalService: MsalService,
//     private http: HttpClient
//   ) {}

//   async setAccessToken(): Promise<void> {
//     /**
//      * 1) Check if there is an active account set. If not, pick the first from getAllAccounts().
//      *    - If getAllAccounts() is empty, user likely hasn’t logged in (or we’re missing an interactive login).
//      * 2) Acquire a token silently using that account.
//      */
//     try {
//       const allAccounts = this.msalService.instance.getAllAccounts();
//       if (!allAccounts || allAccounts.length === 0) {
//         // If empty, the user isn’t actually signed in. 
//         console.error('No accounts found. User must log in first.');
//         return;
//       }

//       // Optionally set the active account if not already set.
//       let activeAccount = this.msalService.instance.getActiveAccount();
//       if (!activeAccount) {
//         // If no active account, pick the first one. 
//         // (In a multi-account scenario, you'd pick more carefully.)
//         activeAccount = allAccounts[0];
//         this.msalService.instance.setActiveAccount(activeAccount);
//       }

//       // Build the silent request
//       // For example, if your scope is "api://<client-id>/user_impersonation", you'd do:
//       //   scopes: ["api://<client-id>/user_impersonation"]
//       // Instead of environment.scopeUri/environment.scope if that’s not giving the correct scope
//       const silentRequest = {
//         scopes: [`${environment.scopeUri}/${environment.scope}`],
//         account: activeAccount,
//       };

//       // Because acquireTokenSilent returns an Observable in MSAL Angular, you can `toPromise()` it
//       // or subscribe explicitly. This is an example with `toPromise()` for clarity:
//       const result = await this.msalService.acquireTokenSilent(silentRequest).toPromise();

//       if (result && result.accessToken) {
//         this.setToken = result.accessToken;
//         this.fetchUserInfo();
//       }
//     } catch (error) {
//       console.error('Error acquiring token silently:', error);
//       // Optionally, you can do a fallback to interactive login or handle the error differently:
//       // this.msalService.acquireTokenRedirect(silentRequest);
//     }
//   }

//   private set setToken(val: string) {
//     this.accessToken = val;
//     // localStorage.setItem('accessToken', val); // If you want to store tokens in local storage (be mindful of security).
//   }

//   get getToken(): string {
//     return this.accessToken;
//     // Or, if you want to fallback to local storage:
//     // return this.accessToken || localStorage.getItem('accessToken') || '';
//   }

//   get getUserInfo(): Observable<any> {
//     return this.userInfo.asObservable();
//   }

//   private fetchUserInfo(): void {
//     if (!this.accessToken) {
//       console.error('No access token found. Unable to fetch user info.');
//       return;
//     }

//     const headers = { Authorization: `Bearer ${this.accessToken}` };
//     this.http.get(`${this.origin}/user-info`, { headers }).subscribe(
//       (info) => this.userInfo.next(info),
//       (error) => console.error('Error fetching user info:', error)
//     );
//   }
// }
